/**
 * Note view — rendered markdown, tags, timestamps, edit action.
 *
 * Also the one page with single-key shortcuts, because it is the page you
 * arrive at and leave from most: `e` edits, `n` starts a new note, `s` opens
 * search, and `a` or the backtick go back to all notes. See `lib/pageKeys`.
 */

import { fetchNote } from "../api/notes";
import type { Note } from "../api/types";
import { attachmentsSection } from "../components/attachments";
import { pageHeader } from "../components/pageHeader";
import { tagList } from "../components/tags";
import { emptyState } from "../components/states";
import { runAsyncView } from "../lib/asyncView";
import { el } from "../lib/dom";
import { formatDateTime } from "../lib/dates";
import { renderMarkdownInto } from "../lib/markdown";
import { bindPageKeys } from "../lib/pageKeys";
import { editNotePath, homePath, newNotePath, searchPath } from "../router/paths";
import { navigate } from "../router/router";
import type { PageContext, PageView } from "../router/router";

function metaRow(label: string, value: string): HTMLElement {
  return el("div", {
    className: "meta__row",
    children: [
      el("dt", { className: "meta__label", text: label }),
      el("dd", { className: "meta__value", text: value }),
    ],
  });
}

function noteView(note: Note, disposers: (() => void)[]): Node {
  const body = el("div", { className: "prose" });
  if (note.body.trim() === "") {
    body.appendChild(el("p", { className: "state__detail", text: "This note has no body yet." }));
  } else {
    renderMarkdownInto(body, note.body);
  }

  const attachments = attachmentsSection(note.noteId);
  disposers.push(attachments.dispose);

  const aside = el("aside", {
    className: "note__aside",
    children: [
      el("dl", {
        className: "meta",
        children: [
          metaRow("Folder", note.folder),
          metaRow("Created", formatDateTime(note.createdAt)),
          metaRow("Updated", formatDateTime(note.updatedAt)),
        ],
      }),
      el("div", {
        className: "note__tags",
        children: [
          el("h2", { className: "note__aside-title", text: "Tags" }),
          tagList(note.tags) ?? el("p", { className: "state__detail", text: "No tags." }),
        ],
      }),
    ],
  });

  return el("div", {
    className: "note",
    children: [
      pageHeader({
        title: note.title,
        actions: [
          el("a", { className: "button button--primary", attrs: { href: editNotePath(note.noteId) }, text: "Edit" }),
        ],
      }),
      el("div", { className: "note__body", children: [body, attachments.element] }),
      aside,
    ],
  });
}

export function notePage(context: PageContext): PageView {
  const noteId = context.params["id"] ?? "";
  const container = el("div", { className: "panel panel--flush" });

  if (noteId === "") {
    return {
      element: el("section", {
        className: "page page--note",
        children: [emptyState("No note selected", "Pick a note from the recent list.")],
      }),
    };
  }

  const disposers: (() => void)[] = [];
  const view = runAsyncView<Note>({
    container,
    loadingLabel: "Loading note…",
    load: (signal) => fetchNote(noteId, signal),
    render: (note) => noteView(note, disposers),
  });

  // Bound from the id in the URL, not the loaded note, so `e` works while
  // the note is still loading and goes to the same place the Edit button
  // would once it has.
  const unbindKeys = bindPageKeys({
    n: () => navigate(newNotePath()),
    e: () => navigate(editNotePath(noteId)),
    s: () => navigate(searchPath()),
    a: () => navigate(homePath()),
    "`": () => navigate(homePath()),
  });

  const element = el("section", {
    className: "page page--note",
    children: [
      el("a", { className: "back-link", attrs: { href: homePath() }, text: "← All notes" }),
      container,
    ],
  });

  return {
    element,
    dispose: (): void => {
      unbindKeys();
      view.dispose();
      disposers.forEach((dispose) => dispose());
    },
  };
}
